import { useMutation, useQuery } from "@tanstack/react-query";

import queryKeys from "libs/react-query/query_key";
import { queryClient } from "libs/react-query/queryClient";

import { GroupUnitEnum, OrderByEnum } from "./skin.dto";

import { BeconAccountTypes, BeconCommonTypes } from "@becon-team/becon-fe-lib/my-types";
import {
  deleteSkinMacroCandidate,
  deleteSkinMicroCandidate,
  getMicroSkinAnalysisTrend,
  getSkinAnalysisResult,
  getSkinAnalysisResultB2B,
  getSkinAnalysisSet,
  getSkinAverageScore,
  getSkinHistoryStatistics,
  getSkinInquiry,
  postSkinAnalysisAnalyze,
  postSkinAnalysisSet,
  putSkinAnalysisSetDuplicate,
  putSkinInquiry,
} from "./skin.handler";
import { useErrorCodeMap } from "libs/react-query/hooks/useErrorCodeMap";

/** POST: 피부 분석 생성 */
export const usePostSkinAnalysisSet = () => {
  return useMutation({
    mutationFn: postSkinAnalysisSet,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.list(userId) });
    },
  });
};

/** POST: 피부 분석 진행 */
export const usePostSkinAnalysisAnalyze = () => {
  return useMutation({
    mutationFn: postSkinAnalysisAnalyze,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.list(userId) });
    },
  });
};

/** GET: 피부 분석 결과 조회 */
export const useGetSkinAnalysisResult = ({
  userId,
  skinAnalysisSetId,
  type,
}: Partial<Parameters<typeof getSkinAnalysisResult>[0]>) => {
  return useQuery({
    queryKey: queryKeys.skin.skinAnalysisSetId(userId, skinAnalysisSetId),
    queryFn: () =>
      userId && skinAnalysisSetId && type
        ? getSkinAnalysisResult({ userId, skinAnalysisSetId, type })
        : Promise.resolve(undefined),
    enabled: !!userId && !!skinAnalysisSetId,
  });
};

/** PUT: 피부 분석 복사 */
export const usePutSkinAnalysisSetDuplicate = () => {
  return useMutation({
    mutationFn: putSkinAnalysisSetDuplicate,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.list(userId) });
    },
  });
};

/** DELETE: 피부 마이크로 후보 삭제 */
export const useDeleteSkinMicroCandidate = () => {
  return useMutation({
    mutationFn: deleteSkinMicroCandidate,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.list(userId) });
    },
  });
};

/** DELETE: 피부 매크로 후보 삭제 */
export const useDeleteSkinMacroCandidate = () => {
  return useMutation({
    mutationFn: deleteSkinMacroCandidate,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.list(userId) });
    },
  });
};

/**GET: 피부 문진 조회 */
export const useGetSkinInquiry = ({ userId }: Partial<Parameters<typeof getSkinInquiry>[0]>) => {
  return useQuery({
    queryKey: queryKeys.skin.inquiry(userId),
    queryFn: () => (userId ? getSkinInquiry({ userId }) : Promise.resolve(undefined)),
    enabled: !!userId,
  });
};

/** PUT: 피부 문진 수정 */
export const usePutSkinInquiry = () => {
  return useMutation({
    mutationFn: putSkinInquiry,
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.skin.inquiry(userId) });
    },
  });
};

/** GET: 피부 날짜별 통계 히스토리 조회 */
export const useGetSkinHistoryStatistics = ({
  accountType = BeconAccountTypes.AccountTypeEnum.B2C,
  subsetType,
  status = BeconCommonTypes.SetStatusEnum.COMPLETED,
  orderBy = OrderByEnum.DESC,
  unit = GroupUnitEnum.DAY,
  userId,
  timeOffset,
  take,
  skip,
}: Partial<Parameters<typeof getSkinHistoryStatistics>[0]>) => {
  return useQuery({
    queryKey: [...queryKeys.skin.stat(userId), subsetType, take, skip],
    queryFn: () =>
      subsetType && userId && timeOffset
        ? getSkinHistoryStatistics({ accountType, subsetType, status, orderBy, userId, unit, timeOffset, take, skip })
        : Promise.resolve(undefined),
    enabled: !!subsetType && !!userId && !!timeOffset,
  });
};

/** GET: 피부 히스토리 조회 */
export const useGetSkinAnalysisSet = ({
  accountType = BeconAccountTypes.AccountTypeEnum.B2C,
  subsetType,
  status,
  subsetStatus,
  userId,
  fromDate,
  toDate,
  skip,
  take,
  timeOffset,
}: Partial<Parameters<typeof getSkinAnalysisSet>[0]>) => {
  return useQuery({
    queryKey: [...queryKeys.skin.list(userId), fromDate, toDate, subsetType, timeOffset, skip, take],
    queryFn: () =>
      accountType && subsetType && userId && timeOffset
        ? getSkinAnalysisSet({
            accountType,
            subsetType,
            status,
            userId,
            fromDate,
            toDate,
            skip,
            take,
            subsetStatus,
            timeOffset,
          })
        : Promise.resolve(undefined),
    enabled: !!subsetType && !!userId && !!accountType && !!timeOffset,
    refetchOnMount: "always",
  });
};

/** GET: 피부 마이크로 분석 트렌드 조회 */
export const useGetMicroSkinAnalysisTrend = ({
  userId,
  timeOffset,
}: Partial<Parameters<typeof getMicroSkinAnalysisTrend>[0]>) => {
  return useQuery({
    queryKey: [...queryKeys.skin.trend(userId), timeOffset],
    queryFn: () =>
      userId && timeOffset ? getMicroSkinAnalysisTrend({ userId, timeOffset }) : Promise.resolve(undefined),
    enabled: !!userId && !!timeOffset,
  });
};

/** GET: 피부 평균 점수 조회 */
export const useGetSkinAverageScore = ({ userId, type }: Partial<Parameters<typeof getSkinAverageScore>[0]>) => {
  return useQuery({
    queryKey: [...queryKeys.skin.averageScore(userId), type],
    queryFn: () => (userId && type ? getSkinAverageScore({ userId, type }) : Promise.resolve(undefined)),
    enabled: !!userId && !!type,
  });
};

/** GET: 피부 분석 결과 조회 */
export const useGetSkinAnalysisB2BResult = ({
  userId,
  skinAnalysisSetId,
}: Partial<Parameters<typeof getSkinAnalysisResultB2B>[0]>) => {
  const errorCodeMap = useErrorCodeMap();

  return useQuery({
    queryKey: queryKeys.skin.skinMicroB2B.skinAnalysisSetId(userId, skinAnalysisSetId),
    queryFn: () =>
      userId && skinAnalysisSetId
        ? getSkinAnalysisResultB2B({ userId, skinAnalysisSetId })
        : Promise.resolve(undefined),
    enabled: !!(userId && skinAnalysisSetId),
    meta: {
      errorCodeMap,
    },
  });
};
